import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Document, documentTable } from "models/schemas";
import { CreateDocumentFields } from "models/schemas";
import { FileMetadata } from "components/input-files/definitions";
import {
  RegisterDocumentRequest,
  RegisterDocumentResponse,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/create-audit-form/definitions";
import {
  DownloadUrlRequest,
  UpdateDocumentsRequest,
  DeleteDocumentsRequest,
} from "./definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { deleteInfo } from "./table-config";
import { FileOperationsProps } from "./definitions";
import { mainApi } from "services/main";
import { DeleteIngestedDocumentsResponse } from "../../pages/gaia-ai/definitions";
import {
  getVersionKey,
  DocumentCollection,
  VersionedDocument,
  createDocumentCollection,
} from "./version-utils";
import { GaiaConstants } from "utils/gaia-constants";

/**
 * Extracts the uniqueKey from a documentId path.
 *
 * The documentId will look like this:
 * - {teamName}/kb/{uniqueKey}/{fileName}
 *
 * @param documentId - The document ID containing the S3 path
 * @returns The extracted uniqueKey, or null if the documentId is malformed
 */
export const extractUniqueKey = (documentId: string): string | null => {
  if (!documentId) {
    return null;
  }

  // Use regex to extract uniqueKey from the path structure
  // Pattern: {teamName}/kb/{uniqueKey}/{fileName}
  const match = documentId.match(/kb\/([^/]+)\/[^/]+/);

  return match ? match[1] : null;
};

export const useFileOperations = (props: FileOperationsProps) => {
  const {
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    updateDocumentForMoveCall,
    lazyDownloadUrlCall,
    ingestDocuments,
    deleteIngestedDocuments,
    addNotification,
    teamName,
    currentUser,
    parentId,
    gaiaObjectId,
    category,
    displayMode,
    setDocs,
    setSelectedItems,
    setSingleDoc,
    setDeleteDocModalIsVisible,
    setDocToRename,
    setFileName,
  } = props;

  const dispatch = useDispatch();

  const getDocDetail = useCallback(
    (
      documentId: string,
      documentType: string,
      docs: documentTable[],
      flatDocs: Document[]
    ) => {
      if (displayMode === "flat") {
        return flatDocs.find((doc) => doc.documentId === documentId) || null;
      }
      return (
        docs
          .find((item) => item.documentId === documentType)
          ?.children.find((child) => child.documentId === documentId) || null
      );
    },
    [displayMode]
  );

  const getFileNames = useCallback(
    (singleDoc: Document | undefined, selectedItems: any[]) => {
      if (!singleDoc) {
        return selectedItems?.map((d: Document) => d.fileName).join(", ") || "";
      }
      return singleDoc?.fileName;
    },
    []
  );

  const downloadSingleDoc = useCallback(
    async (doc: Document) => {
      try {
        if (doc.s3Prefix) {
          let request: DownloadUrlRequest = {
            documentId: doc.documentId,
            preview: false,
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (e) {
        addNotification({
          type: "error",
          header: `File download failed.`,
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification]
  );

  const downloadDocs = useCallback(
    async (selectedItems: any[]) => {
      for (const doc of selectedItems) {
        await downloadSingleDoc(doc);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      setSelectedItems([]);
    },
    [downloadSingleDoc, setSelectedItems]
  );

  const handleDocDownload = useCallback(
    async (
      docId: string,
      docType: string,
      docs: documentTable[],
      flatDocs: Document[]
    ) => {
      await downloadSingleDoc(
        getDocDetail(docId, docType, docs, flatDocs) as Document
      );
    },
    [downloadSingleDoc, getDocDetail]
  );

  const changeDocFileName = useCallback(
    async (editDoc: Document, fileName: string) => {
      try {
        const documentCollection = props.documentCollection;
        const allVersions = documentCollection
          ? documentCollection.getVersionsForDocument(editDoc)
          : [editDoc];

        await updateDocCall({
          documentIds: allVersions.map((v) => v.documentId),
          fileName: fileName,
        }).unwrap();

        if (props.useAtlasEndpoints && props.setAtlasDocuments) {
          props.setAtlasDocuments((prev) =>
            prev.map((doc) =>
              doc.documentId === editDoc.documentId ? { ...doc, fileName } : doc
            )
          );
        }

        setDocToRename(undefined);
        setFileName("");

        addNotification({
          type: "success",
          header:
            editDoc.extentionType === "Link"
              ? "Link updated."
              : "File updated.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header:
            editDoc.extentionType === "Link"
              ? "Link update failed."
              : "File update failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      updateDocCall,
      props.useAtlasEndpoints,
      props.setAtlasDocuments,
      props.setDocumentCollection,
      props.documentCollection,
      setDocToRename,
      setFileName,
      addNotification,
    ]
  );

  const onCreateDoc = useCallback(
    async (
      doc: CreateDocumentFields,
      file: File,
      link: string,
      fileName: string,
      callback: () => void
    ) => {
      let uploadUrlResponse: UploadUrlResponse | null = null;
      try {
        if (file) {
          let request: UploadUrlRequest = {
            prefix: category + "/" + parentId,
            fileName: file.name,
            teamName: teamName,
            securityLevel: "Normal",
          };
          uploadUrlResponse = await uploadUrlCall(request).unwrap();

          if (uploadUrlResponse) {
            await fetch(uploadUrlResponse.url, {
              method: "PUT",
              body: file,
              headers: {
                "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
              },
            });
          }
        }

        const s3Prefix = uploadUrlResponse
          ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${file.name}`
          : "";
        const uploadDocRequest: RegisterDocumentRequest = {
          parentId: parentId,
          teamName: teamName,
          fileName: fileName || getFileNameWithoutExtension(file.name),
          documentType: doc.documentType,
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          externalUrl: link,
          extentionType: file ? getFileExtension(file.name) : "Link",
          ...(file && { size: getFileSize(file.size) }),
        };
        if (gaiaObjectId.startsWith("audit")) {
          uploadDocRequest.auditId = gaiaObjectId;
        } else {
          uploadDocRequest.gaiaObjectId = gaiaObjectId;
        }
        const uploadDocResponse: RegisterDocumentResponse =
          await registerDocCall(uploadDocRequest).unwrap();
        callback();

        const newDoc: Document = {
          ...uploadDocRequest,
          documentId: uploadDocResponse.documentId,
          createdAt: (Number(Date.now().toString()) / 1000).toString(),
          createdBy: currentUser,
          updatedAt: (Number(Date.now().toString()) / 1000).toString(),
          updatedBy: currentUser,
          deleted: false,
        };

        if (displayMode === "grouped") {
          setDocs((prevDocs: documentTable[]) => {
            const updatedDocs = prevDocs.map((item) => {
              if (item.documentId === newDoc.documentType) {
                return {
                  ...item,
                  children: [...item.children, newDoc],
                };
              }
              return item;
            });
            return updatedDocs;
          });
        }

        addNotification({
          type: "success",
          header: file ? "File uploaded." : "Link added.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: file ? "File upload failed." : "Link add failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      parentId,
      gaiaObjectId,
      currentUser,
      displayMode,
      setDocs,
    ]
  );

  const onMultipleFileUpload = useCallback(
    async (files: FileMetadata[], callback: () => void) => {
      try {
        const uploadPromises = files.map(async (fileMetadata) => {
          let uploadUrlResponse: UploadUrlResponse | null = null;

          // Get upload URL
          let request: UploadUrlRequest = {
            prefix: category + "/" + parentId,
            fileName: fileMetadata.file.name,
            teamName: teamName,
            securityLevel: "Normal",
          };
          uploadUrlResponse = await uploadUrlCall(request).unwrap();

          // Upload file to S3
          if (uploadUrlResponse) {
            await fetch(uploadUrlResponse.url, {
              method: "PUT",
              body: fileMetadata.file,
              headers: {
                "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
              },
            });
          }

          // Register document
          const s3Prefix = uploadUrlResponse
            ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${fileMetadata.file.name}`
            : "";
          const uploadDocRequest: RegisterDocumentRequest = {
            parentId: parentId,
            teamName: teamName,
            fileName: fileMetadata.fileName,
            documentType: fileMetadata.documentType,
            securityLevel: "Normal",
            s3Prefix: s3Prefix,
            externalUrl: "",
            extentionType: getFileExtension(fileMetadata.file.name),
            size: getFileSize(fileMetadata.file.size),
          };

          // Add either auditId or gaiaObjectId based on the type
          if (gaiaObjectId.startsWith("audit")) {
            uploadDocRequest.auditId = gaiaObjectId;
          } else {
            uploadDocRequest.gaiaObjectId = gaiaObjectId;
          }

          const uploadDocResponse: RegisterDocumentResponse =
            await registerDocCall(uploadDocRequest).unwrap();

          return {
            ...uploadDocRequest,
            documentId: uploadDocResponse.documentId,
            createdAt: (Number(Date.now().toString()) / 1000).toString(),
            createdBy: currentUser,
            updatedAt: (Number(Date.now().toString()) / 1000).toString(),
            updatedBy: currentUser,
            deleted: false,
          };
        });

        const newDocs = await Promise.all(uploadPromises);

        if (props.useAtlasEndpoints && props.setAtlasDocuments) {
          props.setAtlasDocuments((prevDocs) => [...prevDocs, ...newDocs]);
        } else if (props.setDocumentCollection) {
          props.setDocumentCollection((prev) => {
            let collection = prev || createDocumentCollection([]);
            newDocs.forEach((doc) => {
              collection = collection.addDocument(doc);
            });
            return collection;
          });
        }

        callback();
        addNotification({
          type: "success",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } uploaded successfully.`,
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } failed to upload.`,
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      gaiaObjectId,
      currentUser,
      parentId,
    ]
  );

  const deleteDoc = useCallback(
    async (doc: Document) => {
      if (deleteIngestedDocuments && doc.documentId.match(/.+\/kb\/.+/)) {
        // For Atlas documents, use the Atlas KB delete endpoint
        const atlasRequest = {
          teamName: teamName,
          documents: [doc.documentId],
        };
        const resp: DeleteIngestedDocumentsResponse =
          await deleteIngestedDocuments(atlasRequest).unwrap();
        if (resp.failedDocuments.length > 0) {
          resp.failedDocuments.forEach((d) => {
            console.error(
              `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`
            );
          });
          throw new Error("Failed to delete ingested document");
        }
      } else {
        // For regular documents, use the standard delete endpoint
        let request: DeleteDocumentsRequest = {
          documentIds: [doc.documentId],
        };
        await deleteDocCall(request).unwrap();
      }

      if (displayMode === "grouped") {
        setDocs((prevDocs: documentTable[]) => {
          const updatedDocs = prevDocs.map((item) => {
            if (item.documentId === doc.documentType) {
              return {
                ...item,
                children: item.children.filter(
                  (childDoc) => childDoc.documentId !== doc.documentId
                ),
              };
            }
            return item;
          });
          return updatedDocs;
        });
      }
    },
    [deleteDocCall, displayMode, setDocs]
  );

  const deleteDocs = useCallback(
    async (docs: Document[]) => {
      const ATLAS_BATCH_SIZE = 50;

      try {
        // Check if these are Atlas documents (all docs in a call are the same type)
        const isAtlasDocs =
          deleteIngestedDocuments && docs[0]?.documentId.match(/.+\/kb\/.+/);

        if (isAtlasDocs) {
          // Delete Atlas documents in batches of up to 50
          for (let i = 0; i < docs.length; i += ATLAS_BATCH_SIZE) {
            const batch = docs.slice(i, i + ATLAS_BATCH_SIZE);
            const atlasRequest = {
              teamName: teamName,
              documents: batch.map((doc) => doc.documentId),
            };
            const resp: DeleteIngestedDocumentsResponse =
              await deleteIngestedDocuments(atlasRequest).unwrap();

            if (resp.failedDocuments.length > 0) {
              resp.failedDocuments.forEach((d) => {
                console.error(
                  `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`
                );
              });
              throw new Error("Failed to delete some ingested documents");
            }
          }
        } else {
          // Delete regular documents using batch API
          // Cache invalidation in deleteDocCall triggers refetch automatically
          const request: DeleteDocumentsRequest = {
            documentIds: docs.map((doc) => doc.documentId),
          };
          await deleteDocCall(request).unwrap();
        }

        addNotification({
          type: "success",
          header: deleteInfo(docs, " succeeded"),
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: deleteInfo(docs, " failed"),
          dismissible: true,
          timeout: 10000,
        });
      } finally {
        setSelectedItems([]);
        setSingleDoc(undefined);
        setDeleteDocModalIsVisible(false);
      }
    },
    [
      deleteIngestedDocuments,
      deleteDocCall,
      teamName,
      addNotification,
      setSelectedItems,
      setSingleDoc,
      setDeleteDocModalIsVisible,
    ]
  );

  // Atlas-specific upload function
  const onCreateDocForAtlas = useCallback(
    async (
      files: File[],
      teamName: string,
      callback: () => void,
      replaceExisting: boolean = false,
      existingDocs: Document[] = []
    ) => {
      if (!ingestDocuments) {
        addNotification({
          type: "error",
          header: "Atlas document ingestion not available.",
          dismissible: true,
          timeout: 10000,
        });
        callback();
        return;
      }

      try {
        const items: Array<{
          documentUrl: string;
          size: string;
          replaceExisting: boolean;
        }> = [];

        // Create a Map for O(1) lookup of existing documents by filename
        const existingDocsMap = replaceExisting
          ? new Map(
              existingDocs.map((doc) => [
                doc.fileName + (doc.extentionType || ""),
                doc,
              ])
            )
          : null;

        // Upload each file to S3 and prepare for Atlas ingestion
        for (const file of files) {
          // Check if this file is a duplicate and we're in replace mode
          const existingDoc = existingDocsMap
            ? existingDocsMap.get(file.name) || null
            : null;

          // Extract uniqueKey from existing document if this is a replacement
          const existingUniqueKey = existingDoc
            ? extractUniqueKey(existingDoc.documentId)
            : null;

          // Call UploadPresignUrl with optional uniqueKey parameter
          const request: UploadUrlRequest & { uniqueKey?: string } = {
            prefix: "kb",
            fileName: file.name,
            teamName: teamName,
            securityLevel: "Normal",
            ...(existingUniqueKey && { uniqueKey: existingUniqueKey }),
          };

          const uploadUrlResponse = await uploadUrlCall(request).unwrap();

          if (!uploadUrlResponse) {
            throw new Error("Failed to get upload URL");
          }

          // Upload file to S3 using the presigned URL
          const result = await fetch(uploadUrlResponse.url, {
            method: "PUT",
            body: file,
            headers: {
              "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
            },
          });

          if (result.status !== 200) {
            throw new Error(`S3 upload failed with status ${result.status}`);
          }

          // Extract document URL from presigned URL
          const documentUrl = decodeURIComponent(
            uploadUrlResponse.url.split("?")[0]
          );

          // Add item with documentUrl and replaceExisting flag
          items.push({
            documentUrl: documentUrl,
            size: getFileSize(file.size),
            replaceExisting: !!existingUniqueKey, // Set flag based on whether we used existing key
          });
        }

        // Call IngestDocuments API
        const request = {
          teamName: teamName,
          documentIngestions: {
            items: items,
          },
        };

        await ingestDocuments(request).unwrap();

        addNotification({
          type: "success",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } uploaded to Atlas successfully.`,
          dismissible: true,
          timeout: 10000,
        });

        callback();
      } catch (error) {
        const is400Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 400;
        addNotification({
          type: "error",
          header: is400Error
            ? "Atlas document upload failed - file type is not supported."
            : "Atlas document upload failed.",
          dismissible: true,
          timeout: 10000,
        });
        callback();
      }
    },
    [uploadUrlCall, ingestDocuments, addNotification]
  );

  // Team-level file upload function
  const onCreateTeamFiles = useCallback(
    async (files: File[], teamName: string, callback: () => void) => {
      try {
        const uploadPromises = files.map(async (file) => {
          let uploadUrlResponse: UploadUrlResponse | null = null;

          // Get upload URL
          let request: UploadUrlRequest = {
            prefix: category + "/" + parentId,
            fileName: file.name,
            teamName: teamName,
            securityLevel: "Normal",
          };
          uploadUrlResponse = await uploadUrlCall(request).unwrap();

          // Upload file to S3
          if (uploadUrlResponse) {
            await fetch(uploadUrlResponse.url, {
              method: "PUT",
              body: file,
              headers: {
                "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
              },
            });
          }

          // Register document
          const s3Prefix = uploadUrlResponse
            ? `${category}/${parentId}/${uploadUrlResponse.uniqueKey}/${file.name}`
            : "";
          const uploadDocRequest: RegisterDocumentRequest = {
            parentId: parentId,
            teamName: teamName,
            fileName: getFileNameWithoutExtension(file.name),
            documentType: "Team",
            securityLevel: "Normal",
            s3Prefix: s3Prefix,
            externalUrl: "",
            extentionType: getFileExtension(file.name),
            size: getFileSize(file.size),
          };

          const uploadDocResponse: RegisterDocumentResponse =
            await registerDocCall(uploadDocRequest).unwrap();

          return {
            ...uploadDocRequest,
            documentId: uploadDocResponse.documentId,
            createdAt: (Number(Date.now().toString()) / 1000).toString(),
            createdBy: currentUser,
            updatedAt: (Number(Date.now().toString()) / 1000).toString(),
            updatedBy: currentUser,
            deleted: false,
          };
        });

        await Promise.all(uploadPromises);
        callback();

        addNotification({
          type: "success",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } uploaded successfully.`,
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } failed to upload.`,
          dismissible: true,
          timeout: 10000,
        });
        callback();
      }
    },
    [
      uploadUrlCall,
      registerDocCall,
      teamName,
      addNotification,
      category,
      parentId,
      currentUser,
    ]
  );

  const onCreateFolder = useCallback(
    async (folderName: string) => {
      try {
        const registerFolderRequest: RegisterDocumentRequest = {
          parentId: parentId,
          teamName: teamName,
          fileName: folderName,
          documentType: "Team",
          securityLevel: "Normal",
          s3Prefix: "",
          externalUrl: "",
          extentionType: "Folder",
        };
        await registerDocCall(registerFolderRequest).unwrap();

        addNotification({
          type: "success",
          header: "Folder created successfully.",
          dismissible: true,
          timeout: 10000,
        });
      } catch (e) {
        addNotification({
          type: "error",
          header: "Folder creation failed.",
          dismissible: true,
          timeout: 10000,
        });
        throw e;
      }
    },
    [
      registerDocCall,
      teamName,
      parentId,
      addNotification,
      displayMode,
      setDocs,
      currentUser,
    ]
  );

  const moveFiles = useCallback(
    async (
      files: Document[],
      targetParentId: string,
      documentCollection?: DocumentCollection
    ) => {
      try {
        const allFilesToMove: Document[] = [...files];

        // Add older versions using DocumentCollection
        if (documentCollection) {
          files.forEach((file) => {
            const allVersions = documentCollection.getVersionsForDocument(file);
            const olderVersions = allVersions.filter(
              (v: VersionedDocument) => !v.isLatest
            );
            allFilesToMove.push(...olderVersions);
          });
        }

        await updateDocumentForMoveCall({
          documentIds: allFilesToMove.map((file) => file.documentId),
          parentId: targetParentId,
        }).unwrap();

        setSelectedItems([]);

        addNotification({
          type: "success",
          header: `${files.length} file${
            files.length > 1 ? "s" : ""
          } moved successfully.`,
          dismissible: true,
          timeout: 10000,
        });

        // Calculate delay based on number of files moved
        const baseDelay = 2000;
        const perFileDelay = 750;
        // We should delay max 5s for UX
        const calculatedDelay = Math.min(
          baseDelay + (allFilesToMove.length - 1) * perFileDelay,
          5000
        );
        // Manually invalidate cache
        setTimeout(() => {
          dispatch(mainApi.util.invalidateTags(["Documents"]));
        }, calculatedDelay);
      } catch (e) {
        addNotification({
          type: "error",
          header: "Move files failed.",
          dismissible: true,
          timeout: 10000,
        });
        throw e;
      }
    },
    [updateDocumentForMoveCall, addNotification, dispatch, setSelectedItems]
  );

  return {
    getDocDetail,
    getFileNames,
    downloadSingleDoc,
    downloadDocs,
    handleDocDownload,
    changeDocFileName,
    onCreateDoc,
    onMultipleFileUpload,
    onCreateDocForAtlas,
    onCreateTeamFiles,
    onCreateFolder,
    deleteDoc,
    deleteDocs,
    moveFiles,
  };
};
